import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { DocIconTile, LinkStatusChip } from './DocChips';
import { familyOf } from '../config/families';
import type { DocumentListItem } from '../types/documentCapture.types';

/**
 * Registry column defs (86d3rrfbf). Static columns — no visibility toggle — so this table is not
 * exposed to the React-Compiler/TanStack staleness trap (CLAUDE.md 86d39bx22); rows still render
 * through the shared `DataTableRow` so the row-shell mechanics stay in one place.
 */

/** Philippine-local date, matching the timezone the Scanned/Tran filters are expressed in. */
function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatAmount(amount: number | null): string {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount);
}

export const registryColumns: ColumnDef<DocumentListItem>[] = [
  {
    /**
     * ONE Document column, not Type + Number. Two columns spent ~260px saying what a 25px tile says,
     * and squeezed the number into a width where `DR-000044626` wrapped onto two lines — the number
     * is the single thing people scan this table for. The second line carries the family name in
     * full (the tile's colour alone doesn't teach anyone that INT is "Sales Invoice – NT") plus the
     * version count, which is how a superseded document is spotted without opening it.
     */
    id: 'document',
    header: 'Document',
    size: 300,
    cell: ({ row }) => {
      const { docNumber, prefix, versionCount, pageCount } = row.original;
      const family = familyOf(prefix);
      return (
        <div className="flex items-center gap-2.5">
          <DocIconTile value={prefix} size={30} radius={8} icon={15} />
          <div className="min-w-0">
            <Link
              to={`/d/${docNumber}`}
              className="block truncate font-mono text-[13px] font-semibold text-foreground underline-offset-2 hover:underline"
            >
              {docNumber}
            </Link>
            <div className="flex items-center gap-1 truncate text-[11px] text-muted-foreground">
              {family?.tier === 'treasury' && (
                <Lock className="h-2.5 w-2.5 flex-shrink-0" aria-label="Treasury — restricted" />
              )}
              <span className="truncate">
                {family?.label ?? 'Unclassified'} · {versionCount} version{versionCount === 1 ? '' : 's'}
                {pageCount > 1 && ` · ${pageCount} pages`}
              </span>
            </div>
          </div>
        </div>
      );
    },
  },
  {
    id: 'entity',
    header: 'Entity',
    size: 260,
    cell: ({ row }) => (
      <span className="block truncate text-sm text-foreground" title={row.original.entityName ?? undefined}>
        {row.original.entityName ?? <span className="text-muted-foreground">—</span>}
      </span>
    ),
  },
  {
    id: 'amount',
    header: () => <span className="block text-right">Amount</span>,
    size: 140,
    meta: { cellClassName: 'text-right tabular-nums' },
    cell: ({ row }) => <span className="text-sm text-foreground">{formatAmount(row.original.amount)}</span>,
  },
  {
    id: 'tranDate',
    header: 'Tran date',
    size: 120,
    meta: { cellClassName: 'tabular-nums' },
    cell: ({ row }) => <span className="text-sm text-muted-foreground">{formatDate(row.original.tranDate)}</span>,
  },
  {
    id: 'scannedAt',
    header: 'Scanned',
    size: 120,
    meta: { cellClassName: 'tabular-nums' },
    cell: ({ row }) => <span className="text-sm text-muted-foreground">{formatDate(row.original.scannedAt)}</span>,
  },
  {
    id: 'linkStatus',
    header: 'Link',
    size: 170,
    cell: ({ row }) => (
      <LinkStatusChip
        status={row.original.linkStatus}
        manualLinkedAt={row.original.manualLinkedAt}
        manualLinkedByName={row.original.manualLinkedByName}
      />
    ),
  },
];
